import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Row, Col, Typography, Button, Empty, Divider, Card, Form, Input, Radio, Modal, message,
  Badge, Tooltip, Popconfirm, Tag, Alert, Spin, notification
} from 'antd';
import {
  DeleteOutlined, ShoppingOutlined, ArrowLeftOutlined,
  SendOutlined, WhatsAppOutlined, PlusOutlined, MinusOutlined,
  CloseOutlined, ExclamationCircleOutlined, ShoppingCartOutlined,
  GiftOutlined, InfoCircleOutlined, LoadingOutlined, CheckCircleOutlined,
  CloseCircleOutlined, DisconnectOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import { FixedSizeList } from 'react-window';
import { formatImageUrl, api, API_BASE_URL } from '../../services/api';
import CartProductList from '../../components/CartProductList';
import OrderFormModal from '../../components/OrderFormModal';
import './CartPage.css';

const { Title, Text } = Typography;

const CartPage = ({ cart = [], updateCartItemQuantity, removeFromCart, clearCart }) => {
  const navigate = useNavigate();
  const [isOrderFormVisible, setIsOrderFormVisible] = useState(false);
  const [form] = Form.useForm();
  const [deliveryType, setDeliveryType] = useState('recojo');
  const [animateTotal, setAnimateTotal] = useState(false);
  const [removedItemId, setRemovedItemId] = useState(null);
  const [confetti, setConfetti] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const scrollRef = useRef(null);
  const cartContentRef = useRef(null);

  const calculateTotals = () => {
    let subtotal = 0;
    let discount = 0;
    cart.forEach(item => {
      const itemRegularPrice = item.price * item.quantity;
      subtotal += itemRegularPrice;
      if (item.promotions && item.promotions.length > 0) {
        const promotion = item.promotions[0];
        if (promotion.type === 'descuento') {
          discount += itemRegularPrice * (promotion.value / 100);
        }
      }
    });
    const total = subtotal - discount;
    return { subtotal, discount, total };
  };

  const { subtotal, discount, total } = calculateTotals();

  useEffect(() => {
    if (cart.length > 0 && cartContentRef.current) {
      cartContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isOrderFormVisible]);

  useEffect(() => {
    if (cart.length > 0) {
      setAnimateTotal(true);
      const timer = setTimeout(() => setAnimateTotal(false), 300);
      return () => clearTimeout(timer);
    }
  }, [total, cart.length]);

  const createConfetti = () => {
    const confettiCount = 50;
    const newConfetti = [];
    for (let i = 0; i < confettiCount; i++) {
      newConfetti.push({
        id: `confetti-${Date.now()}-${i}`,
        left: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 0.5}s`,
        size: `${Math.random() * 8 + 5}px`,
        backgroundColor: getRandomColor(),
      });
    }
    setConfetti(newConfetti);
    setTimeout(() => setConfetti([]), 3000);
  };

  const getRandomColor = () => {
    const colors = ['#ff6da0', '#ffb74d', '#64b5f6', '#81c784', '#ba68c8'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const showOrderForm = () => {
    if (cart.length === 0) {
      message.warning('El carrito está vacío');
      return;
    }
    setFormStep(1);
    setIsOrderFormVisible(true);
    createConfetti();
  };

  const handleCancelOrderForm = () => {
    setIsOrderFormVisible(false);
    setFormStep(1);
  };

  const handleSubmitOrder = async (values) => {
    const key = 'orderProcess';
    notification.open({
      key,
      message: 'Procesando pedido',
      description: 'Registrando tu pedido...',
      icon: <LoadingOutlined style={{ color: '#ff6da0' }} />,
      duration: 0,
    });
    try {
      setSubmitLoading(true);
      setSavingOrder(true);
      if (!navigator.onLine) throw new Error('No hay conexión a Internet');
      const orderData = {
        nombre_cliente: values.name,
        telefono: values.phone,
        direccion: values.address || null,
        tipo_entrega: deliveryType,
        items: cart.map(item => ({
          producto_id: item.id,
          cantidad: item.quantity,
          precio: item.price
        })),
        comentarios: values.notes || null,
        monto_total: total
      };
      const response = await api.post('/pedidos', orderData);
      if (response.data) {
        notification.success({
          key,
          message: '¡Pedido registrado!',
          description: (
            <>
              <p>Tu pedido ha sido registrado correctamente</p>
              <small>Código: {response.data.codigo}</small>
            </>
          ),
          icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
          duration: 4,
        });
        let msg = `¡Hola! Mi nombre es ${values.name}\nTeléfono: ${values.phone}\n\nQuiero hacer un pedido de:\n\n`;
        cart.forEach(item => {
          msg += `• ${item.name} x${item.quantity} - Bs. ${(item.price * item.quantity).toFixed(2)}\n`;
        });
        msg += `\nTotal: Bs. ${total.toFixed(2)}\n\nTipo de entrega: ${deliveryType === 'recojo' ? 'Recojo en tienda' : 'Envío a domicilio'}\n`;
        if (deliveryType === 'delivery' && values.address) {
          msg += `Dirección de envío:\n${values.address}\n`;
        }
        msg += "\nEspero su confirmación. ¡Gracias! 🙂";
        const whatsappUrl = `https://wa.me/59174842996?text=${encodeURIComponent(msg)}`;
        window.open(whatsappUrl, "_blank");
        setTimeout(() => {
          setIsOrderFormVisible(false);
          if (clearCart) {
            clearCart();
            message.success({
              content: 'Carrito limpiado correctamente',
              icon: <ShoppingCartOutlined style={{ color: '#ff6da0' }} />
            });
          }
        }, 1500);
      }
    } catch (error) {
      console.error('Error al enviar pedido:', error);
      if (!navigator.onLine) {
        notification.error({
          key,
          message: 'Error de conexión',
          description: 'No hay conexión a Internet. Por favor, verifica tu conexión y vuelve a intentarlo.',
          icon: <DisconnectOutlined style={{ color: '#ff4d4f' }} />,
          duration: 0,
        });
      } else {
        notification.error({
          key,
          message: 'Error al procesar el pedido',
          description: error.response?.data?.error || 'Hubo un problema al registrar el pedido. Por favor, intente nuevamente.',
          icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
          duration: 4,
        });
      }
    } finally {
      setSubmitLoading(false);
      setSavingOrder(false);
    }
  };

  const handleRemoveItem = (id) => {
    setRemovedItemId(id);
    setTimeout(() => { removeFromCart(id); setRemovedItemId(null); }, 300);
  };

  const getDiscountedPrice = (item) => {
    if (item.promotions && item.promotions.length > 0) {
      const promotion = item.promotions[0];
      if (promotion.type === 'descuento') {
        return item.price * (1 - promotion.value / 100);
      }
    }
    return item.price;
  };

  return (
    <div className="cart-page">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="cart-simple-header">
        <motion.div whileHover={{ x: -3 }} whileTap={{ scale: 0.98 }}>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/')} className="back-link">Volver</Button>
        </motion.div>
      </motion.div>

      <Row gutter={[24, 24]} className="cart-content" ref={scrollRef}>
        <Col xs={24} lg={16}>
          <motion.div layout className="cart-items-container">
            <div className="cart-items-list">
              {cart.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#fff', marginTop: 60 }}>
                  <Title level={4} style={{ color: '#fff' }}>Tu carrito está vacío</Title>
                  <Button type="primary" onClick={() => navigate('/')} style={{ marginTop: 16, background: 'var(--primary-fuchsia)' }}>Ir a la tienda</Button>
                </div>
              ) : (
                cart.map(item => (
                  <div className="cart-item-card" key={item.id}>
                    <img src={item.image} alt={item.name} className="cart-item-image" />
                    <div className="cart-item-info">
                      <span className="cart-item-name">{item.name}</span>
                      <span className="cart-item-price">Bs. {item.price.toFixed(2)}</span>
                      <div className="cart-item-quantity-row">
                        <button className="cart-qty-btn" onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)} disabled={item.quantity >= (item.stock || 999)}><PlusOutlined /></button>
                        <span style={{ fontWeight: 700, fontSize: 16 }}>{item.quantity}</span>
                        <button className="cart-qty-btn" onClick={() => updateCartItemQuantity(item.id, item.quantity - 1)} disabled={item.quantity <= 1}><MinusOutlined /></button>
                        <button className="cart-item-remove" onClick={() => removeFromCart(item.id)}><DeleteOutlined /></button>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--secondary-purple-dark)', marginTop: 4, fontWeight: 600 }}>
                        Subtotal: Bs. {(item.price * item.quantity).toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </Col>
        <Col xs={24} lg={8}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="order-summary-wrapper">
            <Card className="order-summary-card">
              <div className="summary-header">
                <div className="summary-title-container">
                  <GiftOutlined className="summary-icon" />
                  <Title level={4} className="summary-title">Resumen del Pedido</Title>
                </div>
                {cart.length > 0 && (<Badge count={cart.length} className="summary-count-badge" style={{ backgroundColor: '#ff6da0' }} />)}
              </div>
              <div className="summary-content">
                <div className="summary-row">
                  <Text className="summary-label">Subtotal:</Text>
                  <Text className="summary-value">{subtotal.toFixed(2)} Bs.</Text>
                </div>
                {discount > 0 && (
                  <motion.div className="summary-row discount-row" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} transition={{ duration: 0.3 }}>
                    <Text className="summary-label discount-label"><GiftOutlined className="discount-icon" /> Descuento:</Text>
                    <Text className="summary-value discount-value">-Bs. {discount.toFixed(2)}</Text>
                  </motion.div>
                )}
                <motion.div className="summary-row total-row" animate={{ scale: animateTotal ? [1, 1.03, 1] : 1 }} transition={{ duration: 0.3 }}>
                  <Text strong className="total-label">Total:</Text>
                  <Text strong className="total-price">{total.toFixed(2)} Bs.</Text>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button type="primary" size="large" block className="checkout-button" onClick={showOrderForm} icon={<WhatsAppOutlined />}>
                    Realizar Pedido
                  </Button>
                </motion.div>
                <div className="secure-payment-info">
                  <InfoCircleOutlined className="info-icon" />
                  <Text type="secondary" size="small">Todos los pagos se realizan directamente al momento de la entrega</Text>
                </div>
              </div>
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="promo-banner">
                <div className="promo-banner-content">
                  <div className="promo-banner-text">
                    <Text strong>¡Envío GRATIS en compras mayores a Bs. 200!</Text>
                    <Text type="secondary">Promoción por tiempo limitado</Text>
                  </div>
                  <div className="promo-banner-icon">
                    <ShoppingOutlined />
                  </div>
                </div>
              </motion.div>
            </Card>
          </motion.div>
        </Col>
      </Row>

      <Modal
        title={<div className="modal-header"><WhatsAppOutlined className="whatsapp-icon" /><span>Finalizar Pedido</span></div>}
        open={isOrderFormVisible}
        onCancel={handleCancelOrderForm}
        footer={null}
        className="order-form-modal"
        closeIcon={<CloseOutlined className="close-modal-icon" />}
        centered
        width={400}
      >
        <OrderFormModal
          form={form}
          deliveryType={deliveryType}
          setDeliveryType={setDeliveryType}
          submitLoading={submitLoading}
          savingOrder={savingOrder}
          handleSubmitOrder={handleSubmitOrder}
        />
      </Modal>
    </div>
  );
};

export default CartPage;
